import React, { useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import Reveal from "../../Reveal";
import { useI18n } from "../../../i18n/LanguageContext";
import { SITE } from "../../../content/site";

const ease = [0.16, 1, 0.3, 1];

export default function CommercialPlatform() {
  const { lang } = useI18n();
  const c = (SITE[lang].homeStory || SITE.en.homeStory).sponsorship;
  const alt = (SITE[lang].events || SITE.en.events).alt;
  return (
    <section data-testid="home-sponsorship">
      <DesktopScene c={c} alt={alt} />
      <MobileScene c={c} alt={alt} />
    </section>
  );
}

/* Desktop: scroll-driven build of the commercial architecture over a real event photo */
function DesktopScene({ c, alt }) {
  const ref = useRef(null);
  const [phase, setPhase] = useState(0);
  const total = c.assets.length + 2; // intro + assets + flow
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    setPhase(Math.max(0, Math.min(total - 1, Math.floor(v * total))));
  });
  const flowPhase = phase === total - 1;
  const visible = Math.min(phase, c.assets.length);

  return (
    <div ref={ref} className="relative hidden lg:block" style={{ height: `${total * 85}vh` }}>
      <div className="sticky top-0 flex h-screen items-center overflow-hidden bg-[#050505]">
        <img src="/events/sponsorship.jpg" alt={alt.sponsorship} loading="lazy" width="1920" height="1280"
          className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/75 to-black/40" />
        <motion.div animate={{ opacity: flowPhase ? 0.94 : 0 }} transition={{ duration: 0.8, ease }}
          className="absolute inset-0 bg-[#070708]" />

        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-12">
          <AnimatePresence mode="wait">
            {!flowPhase ? (
              <motion.div key="assets" exit={{ opacity: 0, y: -30 }} transition={{ duration: 0.6, ease }}
                className="grid grid-cols-12 gap-10">
                <div className="col-span-5">
                  <p className="overline text-white/50">{c.overline}</p>
                  <h2 className="font-display mt-7 text-3xl font-light leading-[1.05] tracking-tightest text-white md:text-5xl">{c.title}</h2>
                  <p className="mt-6 max-w-lg text-base font-light leading-relaxed text-white/70 md:text-lg">{c.text}</p>
                  <div className="mt-12 flex max-w-sm gap-2">
                    {Array.from({ length: total }).map((_, i) => (
                      <span key={i} className={`h-[3px] flex-1 rounded-full transition-colors duration-500 ${i <= phase ? "bg-[#2aa08c]" : "bg-white/15"}`} />
                    ))}
                  </div>
                </div>

                <div className="col-span-6 col-start-7">
                  <div className="mb-8 flex items-baseline justify-between border-b border-white/12 pb-4">
                    <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-[#2aa08c]">{c.assetsLabel}</p>
                    <p data-testid="sponsorship-asset-counter" className="font-display text-sm tracking-[0.2em] text-white/40">
                      {String(visible).padStart(2, "0")} / {String(c.assets.length).padStart(2, "0")}
                    </p>
                  </div>
                  <div>
                    {c.assets.map((g, i) => (
                      <motion.div
                        key={g.k}
                        initial={false}
                        animate={i < visible ? { opacity: 1, x: 0 } : { opacity: 0, x: 32 }}
                        transition={{ duration: 0.7, ease }}
                        data-testid={`sponsorship-asset-${i}`}
                        className="relative border-l border-[#2aa08c]/40 pb-9 pl-8 last:pb-0"
                      >
                        <span className="absolute -left-[5px] top-1 h-[9px] w-[9px] rounded-full bg-[#2aa08c]" />
                        <p className="text-xs font-medium uppercase tracking-[0.3em] text-white/85">{g.k}</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {g.items.map((it) => (
                            <span key={it} className="rounded-full border border-white/15 bg-white/[0.05] px-3.5 py-1.5 text-[13px] font-light text-white/70 backdrop-blur-sm">
                              {it}
                            </span>
                          ))}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div key="flow" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease }}>
                <p className="overline text-white/50">{c.overline}</p>
                <h2 className="font-display mt-6 max-w-3xl text-3xl font-light leading-[1.05] tracking-tightest text-white md:text-5xl">{c.flowLabel}</h2>

                <div className="relative mt-16 grid grid-cols-5 gap-8">
                  <motion.span
                    initial={{ scaleX: 0 }} animate={{ scaleX: 1 }}
                    transition={{ duration: 1.6, ease, delay: 0.3 }}
                    className="absolute left-0 right-0 top-[5px] h-px origin-left bg-gradient-to-r from-[#2aa08c] via-[#2aa08c]/60 to-white/15"
                  />
                  {c.flow.map((f, i) => (
                    <motion.div key={f.k} data-testid={`sponsorship-flow-${i}`}
                      initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.7, ease, delay: 0.35 + i * 0.18 }}
                      className="relative pt-8"
                    >
                      <span className="absolute left-0 top-0 h-[11px] w-[11px] -translate-y-[5px] rounded-full border-2 border-[#2aa08c] bg-[#070708]" />
                      <p className="font-display text-xs tracking-[0.25em] text-[#2aa08c]">{String(i + 1).padStart(2, "0")}</p>
                      <h3 className="font-display mt-3 text-xl font-light leading-snug tracking-tight text-white">{f.k}</h3>
                      <p className="mt-3 text-sm font-light leading-relaxed text-white/50">{f.d}</p>
                    </motion.div>
                  ))}
                </div>

                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 1.3 }}
                  className="mt-16 flex items-end justify-between gap-14 border-t border-white/12 pt-9">
                  <p className="max-w-2xl text-lg font-light leading-relaxed text-white/85 md:text-xl">{c.closing}</p>
                  <p data-testid="sponsorship-proof" className="max-w-xs text-sm font-light leading-relaxed text-white/45">{c.proof}</p>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

/* Mobile: photo header + swipeable inventory rail + vertical flow chain */
function MobileScene({ c, alt }) {
  return (
    <div className="lg:hidden">
      <div className="relative min-h-[68vh] overflow-hidden">
        <img src="/events/sponsorship.jpg" alt={alt.sponsorship} loading="lazy" width="1920" height="1280"
          className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-black/55 to-black/30" />
        <div className="relative flex min-h-[68vh] flex-col justify-end px-6 pb-12">
          <p className="overline text-white/55">{c.overline}</p>
          <h2 className="font-display mt-5 text-3xl font-light leading-[1.05] tracking-tightest text-white sm:text-4xl">{c.title}</h2>
          <p className="mt-5 text-base font-light leading-relaxed text-white/75">{c.text}</p>
        </div>
      </div>

      <div className="bg-[#0a0a0b] pb-20 pt-12">
        <div className="mb-6 flex items-baseline justify-between px-6">
          <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-[#2aa08c]">{c.assetsLabel}</p>
          <span className="flex items-center gap-1.5">
            {c.assets.map((_, i) => (
              <span key={i} className="h-1 w-1 rounded-full bg-white/30" />
            ))}
          </span>
        </div>
        <div data-testid="sponsorship-assets-rail"
          className="flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {c.assets.map((g, i) => (
            <div key={g.k} data-testid={`sponsorship-asset-m-${i}`}
              className="min-w-[76%] snap-center rounded-2xl border border-white/12 bg-white/[0.04] p-6">
              <p className="font-display text-xs tracking-[0.25em] text-[#2aa08c]">{String(i + 1).padStart(2, "0")}</p>
              <p className="mt-2 text-sm font-medium uppercase tracking-[0.25em] text-white">{g.k}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {g.items.map((it) => (
                  <span key={it} className="rounded-full border border-white/15 px-3 py-1.5 text-[13px] font-light text-white/70">{it}</span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-14 px-6">
          <Reveal>
            <h3 className="font-display max-w-sm text-2xl font-light leading-snug tracking-tight text-white">{c.flowLabel}</h3>
          </Reveal>
          <div className="mt-9 border-l border-[#2aa08c]/40 pl-7">
            {c.flow.map((f, i) => (
              <Reveal key={f.k} delay={0.08 * i}>
                <div data-testid={`sponsorship-flow-m-${i}`} className="relative pb-10 last:pb-0">
                  <span className="absolute -left-[32px] top-1 h-[9px] w-[9px] rounded-full bg-[#2aa08c]" />
                  <p className="font-display text-xs tracking-[0.25em] text-[#2aa08c]">{String(i + 1).padStart(2, "0")}</p>
                  <h4 className="font-display mt-2 text-lg font-light tracking-tight text-white">{f.k}</h4>
                  <p className="mt-2 text-sm font-light leading-relaxed text-white/55">{f.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-12 border-t border-white/12 pt-7">
              <p className="text-base font-light leading-relaxed text-white/85">{c.closing}</p>
              <p data-testid="sponsorship-proof-m" className="mt-5 text-sm font-light leading-relaxed text-white/45">{c.proof}</p>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
